import * as React from "react"
import { formatAsPrice, formatPercentage } from "../../../../utils/formatters"
import RightAlignHeaderCell from "../../../common/cells/RightAlignHeaderCell"
import DataTableLegacy from "../../../common/DataTableLegacy"
import { sortString } from "../../../../utils/general"
import type { SpecialPricingSummaryReportItem } from "../../../../graphql/types"
import { Color } from "../../../../constants"

type Props = {
    data: SpecialPricingSummaryReportItem[]
    expandedRowsIds: string[]
    showLoading: boolean
}
type State = {
    expandedRows: string[]
}

const isResultRow = (a: SpecialPricingSummaryReportItem, b: SpecialPricingSummaryReportItem) =>
    a.name === "Total" || b.name === "Total"

const columns = [
    {
        title: "± Pricing Type",
        dataIndex: "name",
        width: "40%",
        sorter: (a, b) => {
            return isResultRow(a, b) ? null : sortString(a.name, b.name)
        },
        render: (text, record: SpecialPricingSummaryReportItem) => <span>{record.name}</span>,
    },
    {
        title: <RightAlignHeaderCell>Amount</RightAlignHeaderCell>,
        width: "20%",
        align: "right",
        sorter: (a, b) => {
            return isResultRow(a, b) ? null : a.quantityPercentage - b.quantityPercentage
        },
        render: (text: string, record: SpecialPricingSummaryReportItem) => {
            if (!record.percentage && record.absolute) {
                const val = record.absolute || 0
                return (
                    <span
                        style={
                            val >= 0
                                ? {
                                      color: Color.PrimaryColor,
                                  }
                                : {
                                      color: Color.Red,
                                  }
                        }>
                        {formatAsPrice(record.absolute)}
                    </span>
                )
            } else if (record.percentage) {
                const val = record.percentage || 0
                return (
                    <span
                        style={
                            val >= 0
                                ? {
                                      color: Color.PrimaryColor,
                                  }
                                : {
                                      color: Color.Red,
                                  }
                        }>
                        {formatPercentage(record.percentage, {
                            places: 1,
                        })}
                    </span>
                )
            }

            return null
        },
    },
    {
        title: <RightAlignHeaderCell>Count</RightAlignHeaderCell>,
        key: "count",
        width: "20%",
        align: "right",
        sorter: (a, b) => {
            return isResultRow(a, b) ? null : a.count - b.count
        },
        render: (text: string, record: SpecialPricingSummaryReportItem) => <span>{record.count}</span>,
    },
    {
        title: <RightAlignHeaderCell>Total $</RightAlignHeaderCell>,
        key: "revenue",
        width: "20%",
        align: "right",
        className: "amount-sorter",
        sorter: (a, b) => {
            return isResultRow(a, b) ? null : a.total - b.total
        },
        render: (text: string, record: SpecialPricingSummaryReportItem) => <span>{formatAsPrice(record.total)}</span>,
    },
]

class SpecialPricingSummaryReportTable extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            expandedRows: props.expandedRowsIds,
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        this.setState({
            expandedRows: nextProps.expandedRowsIds,
        })
    }

    rowExpand = (expanded: boolean, record) => {
        const expandedRows = this.state.expandedRows

        if (expanded && !expandedRows.includes(record.id)) {
            this.setState({
                expandedRows: [...expandedRows, record.id],
            })
        } else if (!expanded && expandedRows.includes(record.id)) {
            this.setState({
                expandedRows: expandedRows.filter(rowId => rowId !== record.id),
            })
        }
    }

    render() {
        return (
            <div className="sales-by-items-table-wrapper">
                <style jsx={true} global={true}>
                    {`
                        .sales-by-items-table-wrapper .category span {
                            font-weight: 700;
                        }

                        .sales-by-items-table-wrapper .product span {
                            font-weight: 500;
                        }

                        .sales-by-items-table-wrapper .level-0 {
                            background-color: rgba(0, 0, 0, 0.02);
                            font-weight: 700;
                        }
                    `}
                </style>
                <DataTableLegacy
                    indentSize={50}
                    pagination={false}
                    loading={this.props.showLoading}
                    className="sales-by-items-table"
                    columns={columns}
                    rowClassName={record => {
                        if (record.level === 0) {
                            return "level-0 category"
                        }

                        return "product"
                    }}
                    dataSource={this.props.data}
                    onExpand={this.rowExpand}
                    expandedRowKeys={this.state.expandedRows}
                    rowKey="id"
                />
            </div>
        )
    }
}

export default SpecialPricingSummaryReportTable
